import { BulkAssignmentOperation, BulkAssignmentStatusData, BulkEmployeeData } from '@/api/apis/case';
import { Uuid } from '@/api/types';
import { ParsedBulkAssignmentMessage } from '../types';
import { isTerminalOperation, mergeOperations } from './bulkAssignmentRun.snapshot';
import { BULK_ASSIGNMENT_START_GRACE_MS } from '../constants';

/**
 * Bulk assignment is a single global saga: while one run is in progress the backend rejects every
 * other request, so the modal is really a distributed lock rendered as UI. That is why server state
 * — not local component state — decides whether it is open, and why messages are never filtered by
 * the initiating user.
 *
 * There is no explicit phase. A run is open exactly when `startedAt` is set, which is also exactly
 * when `correlationId` is set — every way a run can open (starting one, or adopting one from a
 * snapshot) already knows its id.
 */
export type BulkAssignmentRunState = {
  correlationId: string | null;
  /** Floor pinned from the initiating payload so "0 of N" renders before the first snapshot. */
  expectedTotal: number;
  operations: Record<Uuid, BulkAssignmentOperation>;
  relatedEmployees: BulkEmployeeData[];
  /** Non-null exactly while a run is open. */
  startedAt: number | null;
  /** When the run was last seen alive, so a stale "nothing active" answer can be ignored. */
  lastProgressAt: number | null;
  /** Runs already finished or abandoned. A late snapshot must never reopen one. */
  endedCorrelationIds: string[];
};

export type BulkAssignmentRunAction =
  | { type: 'run/start'; correlationId: string; expectedTotal: number; now: number }
  | { type: 'ws/message'; message: ParsedBulkAssignmentMessage; now: number }
  | { type: 'rest/reconcile'; status: BulkAssignmentStatusData | null; fetchedAt: number }
  | { type: 'run/finish' };

/** What both inputs have in common: a complete picture of the run at one moment. */
type RunSnapshot = {
  correlationId: string;
  operations: BulkAssignmentOperation[];
  relatedEmployees: BulkEmployeeData[];
};

const ENDED_HISTORY_LIMIT = 5;

export const initialBulkAssignmentRunState: BulkAssignmentRunState = {
  correlationId: null,
  expectedTotal: 0,
  operations: {},
  relatedEmployees: [],
  startedAt: null,
  lastProgressAt: null,
  endedCorrelationIds: [],
};

export const selectIsRunActive = (state: BulkAssignmentRunState) => state.startedAt !== null;

export const selectProcessedCount = (state: BulkAssignmentRunState) =>
  Object.values(state.operations).filter(isTerminalOperation).length;

export const selectCasesTotal = (state: BulkAssignmentRunState) =>
  Math.max(state.expectedTotal, Object.keys(state.operations).length);

const rememberEnded = (endedCorrelationIds: string[], correlationId: string | null) => {
  if (!correlationId || endedCorrelationIds.includes(correlationId)) return endedCorrelationIds;
  return [...endedCorrelationIds, correlationId].slice(-ENDED_HISTORY_LIMIT);
};

const endRun = (state: BulkAssignmentRunState, correlationId = state.correlationId): BulkAssignmentRunState => ({
  ...initialBulkAssignmentRunState,
  endedCorrelationIds: rememberEnded(state.endedCorrelationIds, correlationId),
});

const isSameRun = (state: BulkAssignmentRunState, correlationId: string) =>
  state.correlationId === null || state.correlationId === correlationId;

const absorbSnapshot = (state: BulkAssignmentRunState, snapshot: RunSnapshot, at: number): BulkAssignmentRunState => {
  const operations = mergeOperations(state.operations, snapshot.operations);

  return {
    ...state,
    correlationId: snapshot.correlationId,
    operations,
    relatedEmployees: snapshot.relatedEmployees.length ? snapshot.relatedEmployees : state.relatedEmployees,
    expectedTotal: Math.max(state.expectedTotal, Object.keys(operations).length, snapshot.operations.length),
    startedAt: state.startedAt ?? at,
    lastProgressAt: at,
  };
};

const endIfGone = (state: BulkAssignmentRunState, fetchedAt: number): BulkAssignmentRunState => {
  const { startedAt, lastProgressAt } = state;

  if (startedAt === null) return state;
  if (lastProgressAt !== null && fetchedAt <= lastProgressAt) return state;
  if (fetchedAt - startedAt < BULK_ASSIGNMENT_START_GRACE_MS) return state;

  return endRun(state);
};

const applyMessage = (
  state: BulkAssignmentRunState,
  { message, now }: { message: ParsedBulkAssignmentMessage; now: number },
): BulkAssignmentRunState => {
  if (state.endedCorrelationIds.includes(message.correlationId)) return state;
  if (!isSameRun(state, message.correlationId)) return state;
  if (message.isCompleted) return endRun(state, message.correlationId);

  return absorbSnapshot(state, message, now);
};

const reconcile = (
  state: BulkAssignmentRunState,
  { status, fetchedAt }: { status: BulkAssignmentStatusData | null; fetchedAt: number },
): BulkAssignmentRunState => {
  if (!status) return endIfGone(state, fetchedAt);
  if (state.endedCorrelationIds.includes(status.correlationId)) return state;
  if (!isSameRun(state, status.correlationId)) return endRun(state);

  return absorbSnapshot(state, status, fetchedAt);
};

export const bulkAssignmentRunReducer = (
  state: BulkAssignmentRunState,
  action: BulkAssignmentRunAction,
): BulkAssignmentRunState => {
  switch (action.type) {
    case 'run/start':
      if (state.endedCorrelationIds.includes(action.correlationId)) return state;
      if (state.correlationId === action.correlationId) {
        return { ...state, expectedTotal: Math.max(state.expectedTotal, action.expectedTotal) };
      }

      return {
        ...initialBulkAssignmentRunState,
        endedCorrelationIds: state.endedCorrelationIds,
        correlationId: action.correlationId,
        expectedTotal: action.expectedTotal,
        startedAt: action.now,
      };

    case 'ws/message':
      return applyMessage(state, action);

    case 'rest/reconcile':
      return reconcile(state, action);

    case 'run/finish':
      return selectIsRunActive(state) ? endRun(state) : state;

    default:
      return state;
  }
};
